import { useMutation } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { TitledPaper } from '@dai/components'
import { Android as AndroidIcon, Apple as AppleIcon } from '@mui/icons-material'
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useState } from 'react'

import {
  addPremiumFreeSubscriptionMutation,
  addPremiumFreeSubscriptionMutationVariables,
  UPDATE_USER,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from '@/graphql'
import { ADD_PREMIUM_FREE_SUBSCRIPTION } from '@/graphql/mutations/AddPremiumFreeSubscription'
import { usePermissions } from '@/hooks/usePermissions'
import useT from '@/i18n/useT'
import { ReactComponent as AppIcon } from '@/vendor/app-icon.svg'

import { AccountSetUpParams } from '../accountSetUpConfig'
import { AccountSetUpStepProps } from '../types'

const Congrats = ({ nextStep, user }: AccountSetUpStepProps) => {
  const { t, T } = useT()
  const { permissions } = usePermissions()
  const { state: urlState } = useUrlSearchParams<AccountSetUpParams>()
  const skipPayment = urlState?.sp === '1' || urlState?.fc === '1'

  const [addPremiumFreeSubscription] = useMutation<
    addPremiumFreeSubscriptionMutation,
    addPremiumFreeSubscriptionMutationVariables
  >(ADD_PREMIUM_FREE_SUBSCRIPTION)

  const [updateOnboardingComplete] = useMutation<
    UpdateUserMutation,
    UpdateUserMutationVariables
  >(UPDATE_USER)

  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleContinue = async () => {
    setLoading(true)
    const userId = user?.id
    const tenantId = user?.tenant?.id
    if (!userId || !tenantId) {
      setLoading(false)
      setSubmitError('Invalid User or Tenant')
      return
    }
    try {
      const mutationPromises = []

      if (!user.onboardingComplete) {
        mutationPromises.push(
          updateOnboardingComplete({
            variables: { data: { id: userId, onboardingComplete: true } },
          }),
        )
      }

      if (skipPayment) {
        Sentry.setTag('sensitive', 'true')
        Sentry.captureMessage(`Tenant ${tenantId} skipped payment`, {
          extra: {
            userId,
            userEmail: user.email,
            paymentCustomerId: user.tenant?.paymentCustomerId,
          },
        })

        mutationPromises.push(
          addPremiumFreeSubscription({ variables: { tenantId } }),
        )
      }
      if (mutationPromises.length > 0) {
        await Promise.all(mutationPromises)
      }
      nextStep()
    } catch (error) {
      console.error('Error during continue mutations:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <TitledPaper
      title={t('congrats.congrats.title')}
      subtitle={t('congrats.congrats.subtitle')}
    >
      <Stack spacing={4} alignItems={'center'}>
        <AppIcon />
        <Stack spacing={2} direction="row" justifyContent="center">
          <Link
            href="https://apps.apple.com/us/app/delicious-ai-cr/id6744659423"
            target="_blank"
            rel="noopener"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'black',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 1,
              textDecoration: 'none',
              '&:hover': {
                bgcolor: '#333',
              },
            }}
          >
            <AppleIcon />
            <Box>
              <T
                keyName="congrats.buttons.downloadOnAppStore"
                params={{
                  i: content => (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ lineHeight: 1 }}
                    >
                      {content}
                    </Typography>
                  ),
                  j: content => (
                    <Typography variant="subtitle2">{content}</Typography>
                  ),
                }}
              />
            </Box>
          </Link>
          <Link
            href="https://play.google.com/store/apps/details?id=com.deliciousai.argos.production&pcampaignid=web_share"
            target="_blank"
            rel="noopener"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'black',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 1,
              textDecoration: 'none',
              '&:hover': {
                bgcolor: '#333',
              },
            }}
          >
            <AndroidIcon />
            <Box>
              <T
                keyName="congrats.buttons.downloadOnGooglePlay"
                params={{
                  i: content => (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ lineHeight: 1 }}
                    >
                      {content}
                    </Typography>
                  ),
                  j: content => (
                    <Typography variant="subtitle2">{content}</Typography>
                  ),
                }}
              />
            </Box>
          </Link>
        </Stack>
        {permissions.CAN_LOGIN_TO_WEB_APP && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleContinue}
            disabled={loading}
          >
            {loading ? 'Loading...' : t('common.continue')}
          </Button>
        )}
        {submitError && <Alert severity="error">{submitError}</Alert>}
      </Stack>
    </TitledPaper>
  )
}

export default Congrats
